import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
// Base theme's full page list — this property's pages.json is overrides-only.
import pagesData from '@vibesites/theme-preferred/pages.json';

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

interface Review {
  rating?: number | string | null;
  review_text?: string | null;
}

/**
 * Upstream feeds sometimes pass literal strings like "false", "null", "none"
 * instead of an empty value — treat those as missing.
 */
function isUsableUrl(value: string | null | undefined): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  if (lower === 'false' || lower === 'true' || lower === 'null' || lower === 'undefined' || lower === 'none' || lower === 'n/a') {
    return false;
  }
  return true;
}

// Only pages shown in navigation (visible !== false), excluding the sitemap
// page itself and template pages (e.g., hyperlocal) whose names contain
// unresolved placeholders like {{h1_title}}
// Preferred Employer Program is defined on the company theme (not base
// theme-preferred pages.json), so append it for The Blakely.
const allPages = [
  ...Object.values(pagesData as Record<string, PageEntry>)
    .filter((p) => p.visible !== false && p.path !== '/sitemap' && !p.template),
  {
    name: 'Preferred Employer Program',
    path: '/preferred-employer-program',
    position: 12,
    visible: true,
  },
].sort((a, b) => a.position - b.position);

export function SitemapContent() {
  // Hide /virtual-tour unless the property has at least one tour with a
  // usable URL (matches VirtualTourEmbed's display rule), and hide /reviews
  // unless the property actually has reviews.
  const virtualTours = useCollectionBinding<VirtualTour>('sitemapcontent.virtual_tours', {
    collection: 'virtual_tours',
  });
  const reviews = useCollectionBinding<Review>('sitemapcontent.reviews', {
    collection: 'reviews',
  });
  const hasVirtualTours = (virtualTours || []).some((t) => isUsableUrl(t.virtual_tour_url));
  const hasReviews = (reviews || []).length > 0;

  const pages = allPages.filter((p) => {
    if (p.path === '/virtual-tour' && !hasVirtualTours) return false;
    if (p.path === '/reviews' && !hasReviews) return false;
    return true;
  });

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {pages.map((page) => (
            <a
              key={page.path}
              href={page.path}
              className="block border border-black rounded px-6 py-4 text-center font-body text-sm text-black hover:bg-black hover:text-white transition-colors"
            >
              {page.name}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SitemapContent',
  displayName: 'Sitemap',
  tokens: {},
  collectionBindings: {
    'sitemapcontent.virtual_tours': {
      label: 'Virtual Tours (visibility check)',
      collection: 'virtual_tours',
    },
    'sitemapcontent.reviews': {
      label: 'Reviews (visibility check)',
      collection: 'reviews',
    },
  },
};

(SitemapContent as any).contentSchema = contentSchema;

export default SitemapContent;
